import jsPDF from 'jspdf';

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Draw a filled rectangle then restore text color to black
const fillRect = (doc, x, y, w, h, r, g, b) => {
  doc.setFillColor(r, g, b);
  doc.rect(x, y, w, h, 'F');
  doc.setTextColor(0, 0, 0);
};

export const generateInvoicePDF = (invoice, customer) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape A4
  const pageWidth  = doc.internal.pageSize.getWidth();  // 297
  const pageHeight = doc.internal.pageSize.getHeight(); // 210
  const margin = 10;
  const cw = pageWidth - margin * 2; // 277mm content width
  const safeCustomer = customer || {};
  const items = invoice.items || [];

  // ── HEADER BAR ───────────────────────────────────────────────────────────
  doc.setFillColor(15, 52, 96);
  doc.rect(0, 0, pageWidth, 16, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(15);
  doc.text('SSP BILLING SYSTEM', margin, 11);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('TAX INVOICE', pageWidth - margin, 11, { align: 'right' });

  // ── INVOICE META ─────────────────────────────────────────────────────────
  let y = 22;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(8.5);
  doc.setFont(undefined, 'bold');
  doc.text(`Invoice No : ${invoice.invoiceNumber || 'N/A'}`, margin, y);
  doc.setFont(undefined, 'normal');
  doc.text(`Date       : ${new Date(invoice.invoiceDate || Date.now()).toLocaleDateString('en-IN')}`, margin + 75, y);
  doc.text(`Payment    : ${invoice.paymentMode || 'Cash'}`, margin + 155, y);

  // ── CUSTOMER BOX ─────────────────────────────────────────────────────────
  y += 6;
  const custBoxH = 16;
  doc.setDrawColor(200, 210, 230);
  doc.setLineWidth(0.4);
  fillRect(doc, margin, y, cw, custBoxH, 248, 251, 255);
  doc.rect(margin, y, cw, custBoxH);
  fillRect(doc, margin, y, cw, 5.5, 224, 234, 248);
  doc.rect(margin, y, cw, custBoxH);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(7);
  doc.setTextColor(15, 52, 96);
  doc.text('BILL TO', margin + 3, y + 4);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8.5);
  const half = cw / 2;
  doc.text(`Name  : ${safeCustomer.fullName    || 'N/A'}`, margin + 3, y + 10);
  doc.text(`Phone : ${safeCustomer.phoneNumber || 'N/A'}`, margin + half + 10, y + 10);
  doc.text(`GSTIN : ${safeCustomer.gstinNumber || 'N/A'}`, margin + 3, y + 15);
  doc.text(`State : ${safeCustomer.state       || 'N/A'}`, margin + half + 10, y + 15);

  // ── ITEMS TABLE ──────────────────────────────────────────────────────────
  // Columns: SL(8)+Product(56)+HSN(22)+Qty(14)+Rate(22)+MRP(22)+Disc%(15)+Taxable(28)+SGST(22)+CGST(22)+IGST(22)+Total(24) = 277
  y += custBoxH + 4;
  const ROW_H = 6.5;
  const HDR_H = 7.5;
  const cols = [
    { label: 'SL',      w: 8,  align: 'left'  },
    { label: 'Product', w: 50, align: 'left'  },
    { label: 'HSN',     w: 22, align: 'left'  },
    { label: 'Qty',     w: 14, align: 'right' },
    { label: 'Rate',    w: 22, align: 'right' },
    { label: 'MRP',     w: 22, align: 'right' },
    { label: 'Disc%',   w: 15, align: 'right' },
    { label: 'Tax%',    w: 14, align: 'right' },
    { label: 'Taxable', w: 24, align: 'right' },
    { label: 'SGST',    w: 22, align: 'right' },
    { label: 'CGST',    w: 22, align: 'right' },
    { label: 'IGST',    w: 22, align: 'right' },
    { label: 'Total',   w: 20, align: 'right' },
  ];

  // Header row
  doc.setFillColor(15, 52, 96);
  doc.rect(margin, y, cw, HDR_H, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(7.5);
  let cx = margin;
  cols.forEach(col => {
    const tx = col.align === 'right' ? cx + col.w - 2 : cx + 2;
    doc.text(col.label, tx, y + 5, { align: col.align });
    cx += col.w;
  });

  const tableStartY = y;
  y += HDR_H;

  let totalCGST = 0, totalSGST = 0, totalIGST = 0, grandSubtotal = 0;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(7.5);

  items.forEach((item, rowIdx) => {
    const qty     = toNum(item.quantity);
    const rate    = toNum(item.rate);
    const mrp     = toNum(item.mrp, rate);
    const cgst    = toNum(item.cgst);
    const sgst    = toNum(item.sgst);
    const igst    = toNum(item.igst);
    const disc    = toNum(item.discount);
    const taxable = rate * qty * (1 - disc / 100);
    const total   = taxable + cgst + sgst + igst;

    totalCGST    += cgst;
    totalSGST    += sgst;
    totalIGST    += igst;
    grandSubtotal += taxable;

    doc.setFillColor(rowIdx % 2 === 0 ? 248 : 240, rowIdx % 2 === 0 ? 250 : 244, 255);
    doc.rect(margin, y, cw, ROW_H, 'F');
    doc.setTextColor(0, 0, 0);

    const taxRate = toNum(item.taxRate);
    const vals = [
      (rowIdx + 1).toString(),
      (item.productName || '').substring(0, 28),
      item.hsnCode || '-',
      qty.toString(),
      `Rs.${rate.toFixed(2)}`,
      `Rs.${mrp.toFixed(2)}`,
      `${disc}%`,
      `${taxRate}%`,
      `Rs.${taxable.toFixed(2)}`,
      `Rs.${sgst.toFixed(2)}`,
      `Rs.${cgst.toFixed(2)}`,
      `Rs.${igst.toFixed(2)}`,
      `Rs.${total.toFixed(2)}`,
    ];

    cx = margin;
    cols.forEach((col, ci) => {
      const tx = col.align === 'right' ? cx + col.w - 2 : cx + 2;
      doc.text(vals[ci], tx, y + 4.8, { align: col.align });
      cx += col.w;
    });

    doc.setDrawColor(210, 218, 230);
    doc.setLineWidth(0.2);
    doc.line(margin, y + ROW_H, margin + cw, y + ROW_H);
    y += ROW_H;
  });

  // Table outer border + vertical separators
  doc.setDrawColor(150, 170, 200);
  doc.setLineWidth(0.5);
  doc.rect(margin, tableStartY, cw, HDR_H + items.length * ROW_H);
  cx = margin;
  cols.forEach((col, ci) => {
    cx += col.w;
    if (ci < cols.length - 1) {
      doc.setDrawColor(200, 210, 225);
      doc.setLineWidth(0.25);
      doc.line(cx, tableStartY, cx, tableStartY + HDR_H + items.length * ROW_H);
    }
  });

  // ── TAX BREAKDOWN + TOTALS ────────────────────────────────────────────────
  y += 5;
  const totalTax   = totalCGST + totalSGST + totalIGST;
  const grandTotal = grandSubtotal + totalTax;

  const taxBoxW = 75;
  let taxY = y;
  const taxRows = [
    { label: 'CGST',      value: totalCGST, bold: false },
    { label: 'SGST',      value: totalSGST, bold: false },
    { label: 'IGST',      value: totalIGST, bold: false },
    { label: 'TOTAL TAX', value: totalTax,  bold: true  },
  ];

  doc.setFillColor(15, 52, 96);
  doc.rect(margin, taxY, taxBoxW, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(7.5);
  doc.text('TAX BREAKDOWN', margin + 3, taxY + 4.2);
  taxY += 6;

  taxRows.forEach((row, ri) => {
    const bg = ri % 2 === 0 ? [240, 246, 255] : [248, 251, 255];
    doc.setFillColor(bg[0], bg[1], bg[2]);
    doc.rect(margin, taxY, taxBoxW, 6, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, row.bold ? 'bold' : 'normal');
    doc.setFontSize(8);
    doc.text(row.label, margin + 3, taxY + 4.2);
    doc.text(`Rs.${row.value.toFixed(2)}`, margin + taxBoxW - 3, taxY + 4.2, { align: 'right' });
    taxY += 6;
  });

  doc.setDrawColor(150, 170, 200);
  doc.setLineWidth(0.4);
  doc.rect(margin, y + 6, taxBoxW, taxRows.length * 6);

  // Totals (right side)
  const totX = margin + taxBoxW + 15;
  const grandBoxW = cw - taxBoxW - 15;
  let totY = y;

  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text('Subtotal', totX, totY + 5);
  doc.text(`Rs. ${grandSubtotal.toFixed(2)}`, margin + cw, totY + 5, { align: 'right' });

  totY += 8;
  doc.text('Total Tax', totX, totY + 5);
  doc.text(`Rs. ${totalTax.toFixed(2)}`, margin + cw, totY + 5, { align: 'right' });

  totY += 7;
  doc.setDrawColor(180, 190, 210);
  doc.setLineWidth(0.4);
  doc.line(totX, totY + 5, margin + cw, totY + 5);

  totY += 8;
  doc.setFillColor(15, 52, 96);
  doc.rect(totX - 2, totY, grandBoxW + 2, 9, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.text('GRAND TOTAL', totX + 1, totY + 6.5);
  doc.text(`Rs. ${grandTotal.toFixed(2)}`, margin + cw - 2, totY + 6.5, { align: 'right' });

  // ── FOOTER ───────────────────────────────────────────────────────────────
  doc.setTextColor(140, 140, 140);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(7);
  doc.text(
    'This is a computer generated invoice. Signature not required.',
    pageWidth / 2, pageHeight - 5, { align: 'center' }
  );

  return doc;
};

export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

export const downloadJSON = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
};

export const generatePurchasePDF = (purchase) => {
  const doc = new jsPDF('p', 'mm', 'a4'); // Portrait A4
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const cw = pageWidth - margin * 2;
  const items = purchase.items || [];

  // ── HEADER BAR ───────────────────────────────────────────────────────────
  doc.setFillColor(15, 52, 96);
  doc.rect(0, 0, pageWidth, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(17);
  doc.text('SSP BILLING SYSTEM', margin, 13);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('PURCHASE RECORD', pageWidth - margin, 13, { align: 'right' });

  // ── PURCHASE META ─────────────────────────────────────────────────────────
  let y = 28;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text(`Purchase No: ${purchase.purchaseNumber || 'N/A'}`, margin, y);
  doc.setFont(undefined, 'normal');
  doc.text(`Date: ${new Date(purchase.purchaseDate || Date.now()).toLocaleDateString('en-IN')}`, margin, y + 5);
  doc.text(`Payment Mode: ${purchase.paymentMode || 'Cash'}`, margin, y + 10);

  // ── SUPPLIER BOX ──────────────────────────────────────────────────────────
  y += 18;
  const supBoxH = 22;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.4);
  fillRect(doc, margin, y, cw, supBoxH, 248, 251, 255);
  doc.rect(margin, y, cw, supBoxH);
  fillRect(doc, margin, y, cw, 7, 224, 234, 248);
  doc.rect(margin, y, cw, supBoxH);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 52, 96);
  doc.text('SUPPLIER DETAILS', margin + 3, y + 5);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8.5);
  doc.text(`Name  : ${purchase.supplierName  || 'N/A'}`, margin + 3, y + 13);
  doc.text(`Phone : ${purchase.supplierPhone || 'N/A'}`, margin + 3, y + 18);
  doc.text(`GSTIN : ${purchase.supplierGSTIN || 'N/A'}`, pageWidth / 2 + 2, y + 13);

  // ── ITEMS TABLE ───────────────────────────────────────────────────────────
  y += supBoxH + 6;
  const ROW_H = 7;
  const HDR_H = 8;
  const cols = [
    { label: '#',        w: 8,  align: 'left'  },
    { label: 'Product Name', w: 52, align: 'left'  },
    { label: 'HSN',      w: 18, align: 'left'  },
    { label: 'Qty',      w: 13, align: 'right' },
    { label: 'Rate',     w: 22, align: 'right' },
    { label: 'Tax %',    w: 14, align: 'right' },
    { label: 'Subtotal', w: 23, align: 'right' },
    { label: 'Tax Amt',  w: 18, align: 'right' },
    { label: 'Total',    w: 14, align: 'right' },
  ];

  doc.setFillColor(15, 52, 96);
  doc.rect(margin, y, cw, HDR_H, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  let cx = margin;
  cols.forEach(col => {
    const tx = col.align === 'right' ? cx + col.w - 2 : cx + 2;
    doc.text(col.label, tx, y + 5.5, { align: col.align });
    cx += col.w;
  });

  const tableStartY = y;
  y += HDR_H;

  let totalCGST = 0, totalSGST = 0, totalIGST = 0, grandSubtotal = 0;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);

  items.forEach((item, rowIdx) => {
    const qty   = toNum(item.quantity);
    const price = toNum(item.purchasePrice);
    const cgst  = toNum(item.cgst);
    const sgst  = toNum(item.sgst);
    const igst  = toNum(item.igst);
    const taxRate = toNum(item.taxRate);
    const taxAmt  = cgst + sgst + igst;
    const subtotal = price * qty;
    const total    = subtotal + taxAmt;

    totalCGST    += cgst;
    totalSGST    += sgst;
    totalIGST    += igst;
    grandSubtotal += subtotal;

    doc.setFillColor(rowIdx % 2 === 0 ? 248 : 240, rowIdx % 2 === 0 ? 250 : 244, 255);
    doc.rect(margin, y, cw, ROW_H, 'F');
    doc.setTextColor(0, 0, 0);

    const vals = [
      (rowIdx + 1).toString(),
      (item.productName || '').substring(0, 28),
      item.hsnCode || '-',
      qty.toString(),
      `Rs.${price.toFixed(2)}`,
      `${taxRate}%`,
      `Rs.${subtotal.toFixed(2)}`,
      `Rs.${taxAmt.toFixed(2)}`,
      `Rs.${total.toFixed(2)}`,
    ];

    cx = margin;
    cols.forEach((col, ci) => {
      const tx = col.align === 'right' ? cx + col.w - 2 : cx + 2;
      doc.text(vals[ci], tx, y + 5, { align: col.align });
      cx += col.w;
    });

    doc.setDrawColor(210, 218, 230);
    doc.setLineWidth(0.2);
    doc.line(margin, y + ROW_H, margin + cw, y + ROW_H);
    y += ROW_H;
  });

  doc.setDrawColor(150, 170, 200);
  doc.setLineWidth(0.5);
  doc.rect(margin, tableStartY, cw, HDR_H + items.length * ROW_H);
  cx = margin;
  cols.forEach((col, ci) => {
    cx += col.w;
    if (ci < cols.length - 1) {
      doc.setDrawColor(200, 210, 225);
      doc.setLineWidth(0.3);
      doc.line(cx, tableStartY, cx, tableStartY + HDR_H + items.length * ROW_H);
    }
  });

  // ── TAX BREAKDOWN + TOTALS ────────────────────────────────────────────────
  y += 8;
  const totalTax  = totalCGST + totalSGST + totalIGST;
  const grandTotal = grandSubtotal + totalTax;
  const taxBoxW = 82;
  let taxY = y;

  const taxRows = [
    { label: 'CGST',      value: totalCGST, bold: false },
    { label: 'SGST',      value: totalSGST, bold: false },
    { label: 'IGST',      value: totalIGST, bold: false },
    { label: 'TOTAL TAX', value: totalTax,  bold: true  },
  ];

  doc.setFillColor(15, 52, 96);
  doc.rect(margin, taxY, taxBoxW, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  doc.text('TAX BREAKDOWN', margin + 3, taxY + 5);
  taxY += 7;

  taxRows.forEach((row, ri) => {
    const bg = ri % 2 === 0 ? [240, 246, 255] : [248, 251, 255];
    doc.setFillColor(bg[0], bg[1], bg[2]);
    doc.rect(margin, taxY, taxBoxW, 6, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, row.bold ? 'bold' : 'normal');
    doc.setFontSize(8);
    doc.text(row.label, margin + 3, taxY + 4.2);
    doc.text(`Rs.${row.value.toFixed(2)}`, margin + taxBoxW - 3, taxY + 4.2, { align: 'right' });
    taxY += 6;
  });

  doc.setDrawColor(150, 170, 200);
  doc.setLineWidth(0.4);
  doc.rect(margin, y + 7, taxBoxW, taxRows.length * 6);

  const totX = margin + taxBoxW + 10;
  const totW = cw - taxBoxW - 10;
  let totY = y;

  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text('Subtotal', totX, totY + 5);
  doc.text(`Rs. ${grandSubtotal.toFixed(2)}`, margin + cw, totY + 5, { align: 'right' });

  totY += 8;
  doc.text('Total Tax', totX, totY + 5);
  doc.text(`Rs. ${totalTax.toFixed(2)}`, margin + cw, totY + 5, { align: 'right' });

  totY += 7;
  doc.setDrawColor(180, 190, 210);
  doc.setLineWidth(0.4);
  doc.line(totX, totY + 5, margin + cw, totY + 5);

  totY += 8;
  doc.setFillColor(15, 52, 96);
  doc.rect(totX - 2, totY, totW + 2, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.text('GRAND TOTAL', totX + 1, totY + 7);
  doc.text(`Rs. ${grandTotal.toFixed(2)}`, margin + cw - 1, totY + 7, { align: 'right' });

  // ── FOOTER ───────────────────────────────────────────────────────────────
  doc.setTextColor(140, 140, 140);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(7.5);
  doc.text(
    'This is a computer generated purchase record. Signature not required.',
    pageWidth / 2, pageHeight - 8, { align: 'center' }
  );

  return doc;
};
